import React from "react";
import A4PageFrame from "./A4PageFrame";
import { fmtNum, fmtPct } from "./formatters";
// Own budget rather than ROWS_PER_PAGE_PORTRAIT, so changing it cannot disturb
// SurfacesList which shares that constant.
//
// 46 is MEASURED, not derived: rendered in Chrome WITH the app's global stylesheet
// applied and compared against the footer element's real top edge. At 19 px per row
// the last page — the one that also carries the Total m² line — fits 46 data rows with
// 18 px to spare; 47 puts the final row 2 px UNDER the footer.
//
// History worth keeping: this briefly shipped as 50, measured without the global
// stylesheet and against a guessed page offset, which is what produced the client's
// "Modules: cutted information between pages" on Espace Tourbillon. Note the earlier
// value of 47 was marginal too (−2 px) — it never showed because this section had no
// pagination at all, so the constant was never exercised. If this number is ever
// revisited, measure with the global CSS present and against the footer, or it will
// look safe and clip anyway.
export const MODULES_ROWS_PER_PAGE = 46;

// Phase 3 (B2): Modules summary — mirrors PDF page 1.
//
// Section contents:
//   - Title "Modules"
//   - Per-module table: Numéro, Module, current m², prior m², Diff., %, Info
//   - Total m² row
//
// Round 5: the client asked to delete the "Répartition par catégorie" donut too
// (it was the last chart in the section pipeline). Modules is now a TABLE-ONLY
// portrait page.
//
// PAGINATION added 2026-08-06. Client (Espace Tourbillon): "Modules: missing 2nd
// page". This section had NO pagination and Preview.js hard-coded MODULES_PAGES = 1,
// so a building with more modules than fit one page silently lost the rest: Espace
// Tourbillon has 148 modules, ~48 rendered, ~100 dropped — AND the "Total m²" row was
// pushed off the page, so the total was invisible too. It went unnoticed because no
// other building has more than 22 modules.
//
// Rows now chunk at MODULES_ROWS_PER_PAGE (measured, see below) and the Total m² row renders on the LAST page only.
// modulesSummaryPageCount() is exported so Preview.js derives the page count from the
// very same chunker — the page-number and bookmark math can never drift from the
// render. The section title only gains "(n/N)" when there IS more than one page, so
// single-page buildings stay byte-identical.
//
// Props
//   data         response from GET /v1/repartition-report/modules-summary
//   building     { name, address }
//   periodLabel  string (e.g. "01.01.2024 - 31.12.2024")
//   periodYears  { current: number, prior?: number } — used for chart legend
//   compareEnabled bool — when false, hides the prior column + Diff + %
//   whiteLabelling, logoUrl  for the page frame
//   pageNumber, totalPages

// Chunk the module rows into page-sized groups. The Total m² row lives on the last
// page, so when the final chunk is completely full it gets a page of its own rather
// than being clipped. Always returns ≥ 1 chunk (an empty building → one page showing
// just the header and the total).
export function splitModulesSummary(data) {
  const modules = data?.modules || [];
  const chunks = [];
  for (let i = 0; i < modules.length; i += MODULES_ROWS_PER_PAGE) {
    chunks.push(modules.slice(i, i + MODULES_ROWS_PER_PAGE));
  }
  if (!chunks.length) chunks.push([]);
  if (chunks[chunks.length - 1].length >= MODULES_ROWS_PER_PAGE) chunks.push([]);
  return chunks;
}

// Page count for Preview.js — derived from the SAME chunker the render uses.
export function modulesSummaryPageCount(data) {
  return splitModulesSummary(data).length;
}

const ModulesSummary = ({
  data,
  building,
  periodLabel,
  periodYears,
  compareEnabled,
  whiteLabelling,
  logoUrl,
  basePageNumber = 1,
  grandTotalPages = 1,
}) => {
  const totals = data?.totals || {};

  const currentYear = periodYears?.current ?? "Current";
  const priorYear   = periodYears?.prior   ?? "Prior";

  const chunks = splitModulesSummary(data);
  return (
    <>
      {chunks.map((chunk, pageIdx) => {
        const isLast = pageIdx === chunks.length - 1;
        return (
    <A4PageFrame
      key={pageIdx}
      building={building}
      periodLabel={periodLabel}
      sectionTitle={chunks.length > 1 ? `Modules (${pageIdx + 1}/${chunks.length})` : "Modules"}
      whiteLabelling={whiteLabelling}
      logoUrl={logoUrl}
      pageNumber={basePageNumber + pageIdx}
      totalPages={grandTotalPages}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 4 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #00033F" }}>
              <th style={th}>Numéro</th>
              <th style={th}>Module</th>
              <th style={thNum}>{currentYear}</th>
              {compareEnabled && <th style={thNum}>{priorYear}</th>}
              {compareEnabled && <th style={thNum}>Diff.</th>}
              {compareEnabled && <th style={thNum}>%</th>}
              <th style={thLeft}>Info</th>
            </tr>
          </thead>
          <tbody>
            {chunk.map((m) => (
              <tr key={m.surface}>
                <td style={td}>{m.surface}</td>
                <td style={td}>{m.name || ""}</td>
                <td style={tdNum}>{fmtNum(m.main_value)}</td>
                {compareEnabled && <td style={tdNum}>{fmtNum(m.compare_value)}</td>}
                {compareEnabled && <td style={tdNum}>{fmtNum(m.diff)}</td>}
                {compareEnabled && <td style={tdNum}>{fmtPct(m.diff_pct)}</td>}
                <td style={td}>{m.unit || ""}</td>
              </tr>
            ))}
            {/* Total m² on the LAST page only — it used to be pushed off the page
                entirely on buildings with many modules. */}
            {isLast && (
            <tr style={{ borderTop: "1px solid #00033F", fontWeight: 700 }}>
              <td style={td} />
              <td style={td}>Total m²</td>
              <td style={tdNum}>{fmtNum(totals.main_value)}</td>
              {compareEnabled && <td style={tdNum}>{fmtNum(totals.compare_value)}</td>}
              {compareEnabled && <td style={tdNum}>{fmtNum(totals.diff)}</td>}
              {compareEnabled && <td style={tdNum}>{fmtPct(totals.diff_pct)}</td>}
              <td style={td} />
            </tr>
            )}
          </tbody>
        </table>
      </A4PageFrame>
        );
      })}
    </>
  );
};

// ─── inline styles (kept inline so html-to-image captures them without CSS files) ───

// M1.1: header bumped from 10pt to 11pt for parity. Body already at 10pt.
const th = { padding: "4px 6px", fontWeight: 600, textAlign: "left", fontSize: 11 };
const thNum = { ...th, textAlign: "right" };
const thLeft = { ...th, textAlign: "left", width: 50 };
// Row height PINNED via lineHeight, 2026-08-07. This was the ONLY paginated template
// not pinning it (ResultsPerModule 14px, CostSummary 11px, ObjectDetail 12px all do),
// so its rows inherited the app's global `body { line-height: 24px }` from
// assets/css/style.css and were TALLER on screen than in an isolated render. That is
// what broke the client's Espace Tourbillon preview ("Modules: cutted information
// between pages"): the budget had been raised against a measurement taken without the
// global stylesheet, so the last rows of page 1 slid underneath the footer band. The
// PDF export happened to look fine, which is why it slipped through.
// 3 + 12 + 3 + 1 = 19 px, matching ROW_HEIGHT.portrait in pageMetrics.js.
const td = { padding: "3px 6px", fontSize: 10, lineHeight: "12px", borderBottom: "1px solid rgba(0,3,63,0.05)" };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

export default ModulesSummary;
